'use client';
import s from './ContactForm.module.scss';
import Container from '@/app/components/layout/Container/Container';
import Input from '@/app/components/ui/inputs/Input/Input';
import InputsGroup from '@/app/components/ui/buttons/InputsGroup/InputsGroup';
import Button from '@/app/components/ui/buttons/Button/Button';
import { Controller } from 'react-hook-form';
import { useContactForm } from '@/hooks/useContactForm';
import RadioGroup from '@/app/components/ui/buttons/RadioButton/RadioGroup';
import SuccessState from '@/app/components/common/forms/ContactForm/SuccessState';
import FreeConsultation from '@/app/components/common/modals/ContactModal/FreeConsultation';
import { useContactFormConfig } from '@/context/ContactFormContext';
import { useEffect, useId, useRef } from 'react';
import { useModal } from '@/context/ModalContext';
import { formatTwoDigits } from '@/lib/utils';
import ThemeMarkdown from '@/app/components/ui/ThemeMarkdown';
import { getAllUtmParams } from '@/lib/utmTracking';
import { getVisitContext } from '@/lib/visitTracking';

const ContactForm = ({ isOnDark = false }) => {
  const recaptchaReady = useRef(false);
  const formId = useId();
  const config = useContactFormConfig();
  const {
    handleSubmit,
    control,
    errors,
    phoneFormatRef,
    setIsSubmitted,
    isSubmitted,
    contactMethods,
    getValues,
  } = useContactForm(config);

  const { buttonDataFields } = useModal();

  useEffect(() => {
    const checkReady = setInterval(() => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        recaptchaReady.current = true;
        clearInterval(checkReady);
      }
    }, 500);
    return () => clearInterval(checkReady);
  }, []);

  const onSubmit = async () => {
    const values = getValues();
    // Get the latest UTM parameters before submission
    const utmParams = getAllUtmParams();

    let token = null;

    try {
      if (
        typeof window.grecaptcha !== 'undefined' &&
        window.grecaptcha.execute
      ) {
        await new Promise((resolve) => window.grecaptcha.ready(resolve));

        token = await window.grecaptcha.execute(
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
          { action: 'submit' }
        );
      } else {
        console.warn(
          '⚠️ grecaptcha is not available. Submitting without token.'
        );
      }
    } catch (err) {
      console.warn('⚠️ reCAPTCHA execution failed:', err);
    }

    setIsSubmitted(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify({
          token,
          ...values,
          pageName: document.title,
          pageUrl: window.location.href,
          ...utmParams, // Include latest UTM parameters
          otherData: JSON.stringify(getVisitContext()),
        }),
      });

      if (!response.ok) {
        console.error('Помилка при надсиланні форми');
        alert('Error submitting the form');
      } else {
        const rawPhone = (values.phone || '').replace(/[^0-9]/g, '');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'convGoogleAds',
          phone: rawPhone ? '+' + rawPhone : '',
        });
      }
    } catch (err) {
      console.error('Помилка:', err);
    }
  };

  const formFields = config?.formFields?.fields || [];

  const shouldSeparateLastField = contactMethods.length <= 2;
  const lastField = shouldSeparateLastField
    ? formFields[formFields.length - 1]
    : null;
  const otherFields = shouldSeparateLastField
    ? formFields.slice(0, -1)
    : formFields;

  return (
    <div className={isOnDark ? 'isFormOnDark' : ''}>
      <div className={s.freeConsultationWrapper}>
        <FreeConsultation
          data={config.disclaimer}
          className={s.freeConsultation}
        />
      </div>

      {isSubmitted && (
        <SuccessState
          delay={2000}
          isLoadModel={true}
          setIsSubmitted={setIsSubmitted}
          title={config.successSubmissionTitle}
          description={config.successSubmissionText}
        />
      )}

      <form
        className={`${s.formFields} ${s.form} ${isSubmitted ? s.isSubmitting : ''}`}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        {otherFields.map((field, index) => {
          const number = formatTwoDigits(index + 1);
          const isRadio = !!field.radioOptions;
          const options = isRadio
            ? field.radioOptions.split('\n').filter(Boolean)
            : [];

          return (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <InputsGroup
                  error={errors[field.name]?.message}
                  label={field.label}
                  required={field.isRequired}
                  number={number}
                  isOnDark={isOnDark}
                >
                  {isRadio ? (
                    <RadioGroup
                      control={control}
                      name={field.name}
                      idPrefix={formId}
                      options={options}
                    />
                  ) : (
                    <Input
                      {...controllerField}
                      type={field.type}
                      placeholder={field.placeholder}
                      required={field.isRequired}
                    />
                  )}
                </InputsGroup>
              )}
            />
          );
        })}

        {shouldSeparateLastField && lastField && (
          <div className={s.formContactRow}>
            <Controller
              name={lastField.name}
              control={control}
              render={({ field }) => (
                <InputsGroup
                  error={errors[lastField.name]?.message}
                  label={lastField.label}
                  required={lastField.isRequired}
                  number={formatTwoDigits(formFields.length)}
                  isOnDark={isOnDark}
                >
                  <Input
                    {...field}
                    type={lastField.type}
                    placeholder={lastField.placeholder}
                    required={lastField.isRequired}
                    onChangePhone={
                      lastField.name === 'phone'
                        ? (value, data) => {
                            field.onChange(value);
                            phoneFormatRef.current = data.format;
                          }
                        : undefined
                    }
                  />
                </InputsGroup>
              )}
            />

            <InputsGroup
              error={errors.contactMethod?.message}
              required
              isOnDark={isOnDark}
            >
              <RadioGroup
                control={control}
                name="contactMethod"
                idPrefix={formId}
                options={contactMethods}
              />
            </InputsGroup>
          </div>
        )}

        {!shouldSeparateLastField && (
          <InputsGroup
            error={errors.contactMethod?.message}
            required
            isOnDark={isOnDark}
          >
            <RadioGroup
              control={control}
              name="contactMethod"
              idPrefix={formId}
              options={contactMethods}
            />
          </InputsGroup>
        )}

        <div className={s.footer}>
          <Button
            type="submit"
            className={s.submitButton}
            secondaryLayerColor="secondaryLayerBlack"
          >
            {isSubmitted
              ? config.submitButtonTextLoading
              : config.submitButtonText}
          </Button>
          {config.agreementMessage && (
            <ThemeMarkdown
              className="textGrey textCenter"
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer">
                    {props.children}
                  </a>
                ),
              }}
            >
              {config.agreementMessage}
            </ThemeMarkdown>
          )}
        </div>

        {buttonDataFields &&
          Object.entries(buttonDataFields).map(([key]) => (
            <Controller
              key={key}
              name={key}
              control={control}
              render={({ field }) => <input type="hidden" {...field} />}
            />
          ))}
      </form>
    </div>
  );
};

export default ContactForm;
